import {
  LayoutDashboard, Boxes, Store, ShoppingCart, Megaphone, Radio, GraduationCap,
  Building2, Plug, BarChart3, BellRing, Activity, ShieldCheck, Cpu, Eye, Users,
} from "lucide-react";

// Grouped, collapsible capability registry. Every verified active /admin function is
// mapped into a Command Center category (deep-links preserve the existing protected
// /admin surface). Future modules use route/contract + feature flags — NO duplicate
// engines. Retired Wholesale/Dropshipping surfaces are intentionally absent.
//
// item.type:
//   panel    → renders an internal CC panel (ACTIVE)
//   admin    → deep-links to /admin?section=<section> (ACTIVE, perm-gated)
//   contract → future module surface; status derived from a feature flag (no fake data)

export const NAV_GROUPS = [
  {
    id: "executive", label: "Executive Overview", icon: LayoutDashboard,
    items: [{ id: "overview", label: "Executive Overview", type: "panel", panelKey: "overview" }],
  },
  {
    id: "intelligence", label: "Digital Twin / AEGIS", icon: Cpu,
    items: [
      { id: "aegis", label: "AEGIS Digital Twin", type: "contract", flag: "AEGIS_COMMAND_CENTER_INTEGRATION_ENABLED", panelKey: "aegis" },
      { id: "frontpage", label: "Front Page Preview", type: "contract", flag: "FRONT_PAGE_PREVIEW_ENABLED", disabledStatus: "NOT_CONNECTED" },
    ],
  },
  {
    id: "ecosystem", label: "Marketplaces / Sellers / Stores", icon: Store,
    items: [
      { id: "marketplaces", label: "Marketplaces", type: "contract", disabledStatus: "NOT_CONNECTED" },
      { id: "vendors", label: "Seller Center", type: "admin", section: "vendors", perm: "manage_sellers" },
      { id: "applications", label: "Applications", type: "admin", section: "applications", perm: "manage_sellers" },
      { id: "aistores", label: "Store Creator", type: "admin", section: "aistores", perm: "manage_settings" },
      { id: "membership", label: "Memberships", type: "admin", section: "membership", perm: "manage_settings" },
    ],
  },
  {
    id: "commerce", label: "Commerce & Finance", icon: ShoppingCart,
    items: [
      { id: "commerce", label: "Commerce KPIs", type: "panel", panelKey: "commerce" },
      { id: "financial", label: "Financial", type: "panel", panelKey: "financial" },
      { id: "orders", label: "Orders", type: "panel", panelKey: "orders" },
      { id: "analytics-feed", label: "Shop the Feed", type: "admin", section: "analytics", perm: "manage_sellers" },
      { id: "priceguard", label: "Price Guard", type: "admin", section: "priceguard", perm: "approve_products" },
      { id: "needsreview", label: "Needs Review", type: "admin", section: "needsreview", perm: "approve_products" },
    ],
  },
  {
    id: "marketing", label: "Marketing / Advertising / Networking", icon: Megaphone,
    items: [
      { id: "promotionstudio", label: "Promotion Studio", type: "admin", section: "promotionstudio", perm: "manage_promotions" },
      { id: "promotions", label: "Spin & Save", type: "admin", section: "promotions", perm: "manage_promotions" },
      { id: "gifts", label: "Gifts & Coins", type: "admin", section: "gifts", perm: "manage_promotions" },
      { id: "email", label: "Email Center", type: "admin", section: "email", perm: "manage_promotions" },
    ],
  },
  {
    id: "liveverse", label: "LiveVerse", icon: Radio,
    items: [
      { id: "live", label: "Live Commerce", type: "admin", section: "live", perm: "manage_sellers" },
      { id: "liveverse-summary", label: "LiveVerse Executive Summary", type: "contract", flag: "LIVEVERSE_EXECUTIVE_SUMMARY_ENABLED", panelKey: "liveverse" },
    ],
  },
  {
    id: "academy", label: "YEDA Academy", icon: GraduationCap,
    items: [
      { id: "courses", label: "Learning Center", type: "admin", section: "courses", perm: "manage_courses" },
      { id: "yeda", label: "YEDA Guide", type: "admin", section: "yeda", perm: "manage_settings" },
      { id: "academy-summary", label: "Academy Executive Summary", type: "contract", flag: "ACADEMY_EXECUTIVE_SUMMARY_ENABLED" },
    ],
  },
  {
    id: "offices", label: "Offices & Governance", icon: Building2,
    items: [
      { id: "tegob-offices", label: "TAVEN Offices", type: "contract", flag: "OFFICES_TASK_ENGINE_ENABLED", panelKey: "tegob-offices", disabledStatus: "NOT_IMPLEMENTED" },
      { id: "tegob-tasks", label: "Task Management", type: "contract", flag: "OFFICES_TASK_ENGINE_ENABLED", panelKey: "tegob-tasks", disabledStatus: "NOT_IMPLEMENTED" },
      { id: "tegob-responses", label: "Office Responses", type: "contract", flag: "OFFICES_TASK_ENGINE_ENABLED", panelKey: "tegob-responses", disabledStatus: "NOT_IMPLEMENTED" },
    ],
  },
  {
    id: "connectors", label: "Connector Hub / Supplier Integration", icon: Plug,
    items: [
      { id: "connectors", label: "Connector Health", type: "panel", panelKey: "connectors" },
      { id: "inventory", label: "Inventory Control", type: "panel", panelKey: "inventory" },
      { id: "products", label: "Product Control", type: "panel", panelKey: "products" },
      { id: "cjautomation", label: "Supplier Integration", type: "admin", section: "cjautomation", perm: "manage_settings" },
      { id: "sellersources", label: "Seller Sources", type: "admin", section: "sellersources", perm: "manage_settings" },
      { id: "sellerintegration", label: "Seller Integration", type: "admin", section: "sellerintegration", perm: "manage_settings" },
      { id: "catalog", label: "Catalog & Rotation", type: "admin", section: "catalog", perm: "manage_settings" },
      { id: "cataloghealth", label: "Catalog Health", type: "admin", section: "cataloghealth", perm: "manage_settings" },
      { id: "live-inventory", label: "Live Inventory", type: "admin", section: "inventory", perm: "manage_settings" },
      { id: "inventoryalerts", label: "Inventory Alerts", type: "admin", section: "inventoryalerts", perm: "manage_settings" },
      { id: "pricingrules", label: "Pricing Rules", type: "admin", section: "pricingrules", perm: "manage_settings" },
      { id: "collectionmgr", label: "Collection Display", type: "admin", section: "collectionmgr", perm: "manage_settings" },
      { id: "scheduler", label: "Weekly Scheduler", type: "admin", section: "scheduler", perm: "manage_settings" },
    ],
  },
  {
    id: "analytics", label: "Analytics", icon: BarChart3,
    items: [{ id: "activity", label: "Activity Feed", type: "panel", panelKey: "activity" }],
  },
  {
    id: "alerts", label: "Alerts & Incidents", icon: BellRing,
    items: [
      { id: "alerts", label: "Alert Center", type: "panel", panelKey: "alerts" },
      { id: "approvals", label: "Approval Center", type: "panel", panelKey: "approvals" },
    ],
  },
  {
    id: "health", label: "System Health", icon: Activity,
    items: [{ id: "system-health", label: "System Health", type: "panel", panelKey: "system-health" }],
  },
  {
    id: "omada", label: "OMADA Release Status", icon: ShieldCheck,
    items: [{ id: "omada", label: "OMADA Release Status", type: "panel", panelKey: "omada" }],
  },
  {
    id: "administration", label: "Administration & Identity", icon: Users,
    items: [
      { id: "users", label: "Users & Roles", type: "admin", section: "users", perm: "manage_users" },
      { id: "roles", label: "Permission Matrix", type: "admin", section: "roles", perm: "manage_roles" },
      { id: "updates", label: "Update Center", type: "admin", section: "updates", perm: "manage_settings" },
      { id: "legal", label: "Legal Center", type: "admin", section: "legal", perm: "manage_legal" },
      { id: "continuity", label: "Business Continuity", type: "admin", section: "continuity", perm: "manage_sellers" },
      { id: "featurehub", label: "Feature Hub", type: "admin", section: "featurehub", perm: "manage_settings" },
      { id: "reviews", label: "Reviews", type: "admin", section: "reviews", perm: "manage_reviews" },
      { id: "tickets", label: "Support Tickets", type: "admin", section: "tickets", perm: "manage_tickets" },
      { id: "leads", label: "Leads & Records", type: "admin", section: "leads", perm: "view_dashboard" },
      { id: "security", label: "Security", type: "admin", section: "security", perm: "view_dashboard" },
    ],
  },
];

// Resolve a nav item's status given feature flags + the admin's permission set.
export function resolveItemStatus(item, flags, adminPerms) {
  if (item.type === "panel") return "ACTIVE";
  if (item.type === "admin") {
    if (adminPerms && item.perm && !adminPerms.has(item.perm)) return "ACCESS_RESTRICTED";
    return "ACTIVE";
  }
  // contract
  if (item.flag) {
    const on = flags && flags[item.flag];
    if (on) return "ACTIVE";
    return item.disabledStatus || "FEATURE_DISABLED";
  }
  return item.disabledStatus || "NOT_CONNECTED";
}

export { Eye, Boxes };
